import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MessageCircleQuestion, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { apiFetch } from '@/hooks/useFetch';

/**
 * Admin widget showing the most-asked Detective Dino questions in the
 * selected time window. Surfaces UX gaps and FAQ-worthy queries.
 */
export const HelpBotInsightsCard = () => {
  const [days, setDays] = useState('7');
  const [data, setData] = useState({ top: [], total_questions: 0 });
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    setLoading(true);
    const { data, error } = await apiFetch(`/api/help/admin/top-questions?days=${days}&limit=10`);
    if (!error && data) setData(data);
    setLoading(false);
  };

  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [days]);

  return (
    <Card className="mb-8" data-testid="help-bot-insights-card">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <div>
          <CardTitle className="flex items-center gap-2 text-lg">
            <MessageCircleQuestion className="h-5 w-5 text-[#00E5CC]" />
            Detective Dino — top questions
          </CardTitle>
          <CardDescription>
            What users are actually asking the help bot.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[120px]" data-testid="help-bot-days-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last day</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetch} aria-label="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : data.top.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No questions asked in this window yet.
          </p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground mb-3">
              <strong>{data.total_questions}</strong> total questions in the last {days} day{days === '1' ? '' : 's'}
            </p>
            <ol className="space-y-2">
              {data.top.map((q, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 p-2 rounded hover:bg-muted/50 transition-colors"
                  data-testid={`help-bot-q-${i}`}
                >
                  <Badge
                    variant="outline"
                    className="shrink-0 mt-0.5 bg-[#00E5CC]/10 text-[#00A896] border-[#00E5CC]/30"
                  >
                    {q.count}×
                  </Badge>
                  <p className="text-sm leading-snug flex-1 min-w-0">
                    <span className="line-clamp-2">{q.question}</span>
                    {q.last_asked && (
                      <span className="block text-[10px] text-muted-foreground mt-0.5">
                        last asked {new Date(q.last_asked).toLocaleString()}
                      </span>
                    )}
                  </p>
                </li>
              ))}
            </ol>
            <p className="text-[11px] text-muted-foreground mt-4 italic">
              Tip: Top entries that aren't already in the FAQ are great candidates to add to <code>/faqs</code>.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default HelpBotInsightsCard;
